import React, { useState } from 'react';
import { Alert, Grid, IconButton, InputAdornment } from '@mui/material';
import { useForm } from 'react-hook-form';
import { TextField } from '@/components/forms/inputs/TextField/TextField';
import { accountFormModel } from '@/components/forms/forms/account/accountFormModel';
import { AccountTabs } from '@/types/enums/forms/account/accountTabs';
import { usePhrase, useCompletionStatus, useServerErrors } from '@/hooks';
import { accountService } from '@/services/api';
import {
    ACCOUNT_GENERAL_PHRASES,
    ACCOUNT_PASSWORD_CHANGE_PHRASES,
} from 'consts/phrases/accountPhrases';
import { IAccountPasswordChange } from 'types/types';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';
import { SaveButtonWithCompletionStatus } from 'components/general/buttons/SaveButton/SaveButtonWithCompletionStatus';
import { PasswordRequirements } from 'components/forms/other/PasswordRequirements/PasswordRequirements';
import { CompletionStatus } from 'types/enums/completionStatus';
import { Icon } from '@/components/general/Icon/Icon';

export const AccountPasswordChangeForm = () => {
    const { t } = usePhrase();
    const { completionStatus, getCompletionStatus, resetCompletionStatus } = useCompletionStatus();

    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showNewPasswordConfirm, setShowNewPasswordConfirm] = useState(false);

    const { currentPassword, newPassword, confirmNewPassword } =
        accountFormModel[AccountTabs.PasswordChange];

    const { control, handleSubmit, formState, setError, getValues, clearErrors } = useForm<
        Partial<IAccountPasswordChange>
    >({
        mode: 'onChange',
    });

    const { handleServerErrors, serverErrors, clearAllErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });

    const handleMouseDownPassword = (evt: React.MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
    };

    const submitData = async (values: Partial<IAccountPasswordChange>) => {
        try {
            await getCompletionStatus(() => accountService.passwordChange(values));
            clearAllErrors();
        } catch (err) {
            handleServerErrors(err);
        }
    };

    const { errors, isSubmitting } = formState;

    return (
        <>
            {completionStatus === CompletionStatus.SUCCEEDED ? (
                <Grid container spacing={4}>
                    <Grid size={12}>
                        <Alert severity="success" id="sucess_message_box">
                            {t(ACCOUNT_PASSWORD_CHANGE_PHRASES.PASSWORD_UPDATED_TEXT)}
                        </Alert>
                    </Grid>
                </Grid>
            ) : (
                <form onSubmit={handleSubmit(submitData)} noValidate>
                    <Grid container spacing={4}>
                        <FormGridErrors
                            errors={serverErrors}
                            gridSx={{ mb: 1 }}
                            generalErrorId="error_message_box"
                        />
                        <Grid size={{ xs: 12, md: 7 }}>
                            <Grid container spacing={2}>
                                <Grid size={12}></Grid>
                                <Grid size={12}>
                                    <TextField
                                        control={control}
                                        name={currentPassword.name}
                                        label={currentPassword.label}
                                        id="current_password_field"
                                        type={showCurrentPassword ? 'text' : 'password'}
                                        required
                                        fullWidth
                                        size="small"
                                        error={errors.currentPassword}
                                        data-testid="currentpassword-input"
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={() =>
                                                                setShowCurrentPassword(
                                                                    (prevState) => !prevState,
                                                                )
                                                            }
                                                            onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                        >
                                                            {showCurrentPassword ? (
                                                                <Icon path="general/controlIcons/VisibilityOffIcon" />
                                                            ) : (
                                                                <Icon path="general/controlIcons/VisibilityIcon" />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid size={12}></Grid>
                                <Grid size={12}>
                                    <TextField
                                        control={control}
                                        name={newPassword.name}
                                        label={newPassword.label}
                                        id="new_password_field"
                                        type={showNewPassword ? 'text' : 'password'}
                                        required
                                        fullWidth
                                        size="small"
                                        error={errors.newPassword}
                                        data-testid="newpassword-input"
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={() =>
                                                                setShowNewPassword(
                                                                    (prevState) => !prevState,
                                                                )
                                                            }
                                                            onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                        >
                                                            {showNewPassword ? (
                                                                <Icon path="general/controlIcons/VisibilityOffIcon" />
                                                            ) : (
                                                                <Icon path="general/controlIcons/VisibilityIcon" />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </Grid>
                                <Grid size={12}>
                                    <TextField
                                        control={control}
                                        name={confirmNewPassword.name}
                                        label={confirmNewPassword.label}
                                        id="confirm_new_password_field"
                                        type={showNewPasswordConfirm ? 'text' : 'password'}
                                        required
                                        fullWidth
                                        size="small"
                                        error={errors.confirmNewPassword}
                                        data-testid="confirmNewPassword-input"
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            aria-label="toggle password visibility"
                                                            onClick={() =>
                                                                setShowNewPasswordConfirm(
                                                                    (prevState) => !prevState,
                                                                )
                                                            }
                                                            onMouseDown={handleMouseDownPassword}
                                                            edge="end"
                                                        >
                                                            {showNewPasswordConfirm ? (
                                                                <Icon path="general/controlIcons/VisibilityOffIcon" />
                                                            ) : (
                                                                <Icon path="general/controlIcons/VisibilityIcon" />
                                                            )}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid
                                container
                                sx={{
                                    justifyContent: 'flex-end',
                                    marginTop: 2,
                                }}
                            >
                                <SaveButtonWithCompletionStatus
                                    id="submit-password-change"
                                    label={ACCOUNT_GENERAL_PHRASES.SAVE_BUTTON}
                                    isSubmitting={isSubmitting}
                                    status={completionStatus}
                                    resetStatus={resetCompletionStatus}
                                />
                            </Grid>
                        </Grid>
                        <Grid size={{ xs: 12, md: 5 }}>
                            <Grid container spacing={2}>
                                <Grid size={12}></Grid>
                                <Grid size={12}>
                                    <PasswordRequirements />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </form>
            )}
        </>
    );
};
